import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import { List, CaretLeft, Check, Plus } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { queryKeys } from '../queries/keys';
import Sidebar from '../components/Sidebar';
import SearchBox from '../components/SearchBox';
import EntityDetailModal from '../components/EntityDetailModal';
import ItemDetailModal from '../components/ItemDetailModal';
import UploadModal from '../components/UploadModal';
import EmptyState from '../components/EmptyState';
import { CardGridSkeleton } from '../components/Skeleton';
import Toast from '../components/Toast';
import SelectionBar from '../components/SelectionBar';
import CollectionPickerModal from '../components/CollectionPickerModal';
import SearchSummary from '../components/SearchSummary';
import { cardAccessibleName } from '../utils/accessibleName';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { useUrlModalParam } from '../hooks/useUrlModalParam';
import { useSearch } from '../hooks/useSearch';

const PAGE_SIZE = 100;
const MAX_PAGES = 20;

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name', label: 'Name (A-Z)' },
  { value: 'type', label: 'Type (A-Z)' },
  { value: 'mentions', label: 'Most Mentions' },
  { value: 'importance', label: 'Most Important' },
];

// COL-49 v2: salience score = mention count (v1's proxy) x mean prominence
// weight, where prominence weight is derived from the analyzer's own
// per-item entity ranking (Enrichment.original_rank - "was this entity the
// subject of the item or incidental", already emitted by the live analyzer
// prompt's Entity Ranking Guidelines; no prompt change needed, see COL-49).
// Frequency stays the dominant signal (an entity mentioned once, however
// prominently, must not outrank a real recurring entity) while prominence
// acts as a multiplicative modifier: 1/rank means rank 1 contributes full
// weight (1.0) and lower ranks contribute proportionally less.
function prominenceWeight(originalRank, neutralFallback) {
  // Null original_rank means pre-migration data with no prominence judgment
  // recorded (see model comment on Enrichment.original_rank) - NOT "assume
  // this was the top-ranked entity". Falling back to a weight of 1.0 (rank 1)
  // would silently float every legacy enrichment to the top of an importance
  // sort. Use the dataset's own observed mean instead - a neutral "we don't
  // know, assume average" value that adapts to whatever the actual
  // distribution looks like, rather than a guessed constant.
  if (typeof originalRank !== 'number' || originalRank <= 0) {
    return neutralFallback;
  }
  return 1 / originalRank;
}

// Grouping key (COL-94): entity_type + normalized entity_name is the only
// stable identity for a generic entity — there's no canonical entity_id in
// the schema (see shared/database/models.py Enrichment). Places additionally
// carry a google_place_id, which is preferred when present since place-type
// entities remain browsable inside this page (not just the dedicated /places
// page), and it dedupes better than name text for those.
function groupKey(e) {
  if (e.entity_type === 'place' && e.place_data?.google_place_id) {
    return `place:${e.place_data.google_place_id}`;
  }
  return `${e.entity_type}:${(e.entity_name || '').toLowerCase().trim()}`;
}

// The subset of an enrichment's fields the page/modal need to point back at
// its source item — shared by enrichmentToEntityGroup and the groupedEntities
// builder below so the row shape lives in one place.
function sourceFromEnrichment(e) {
  return {
    item_id: e.item_id,
    item_headline: e.item_headline,
    item_image: e.item_image,
    item_summary: e.item_summary,
    enrichment_id: e.enrichment_id,
  };
}

// Convert a single enrichment into the grouped shape used by the page and
// modal — used for search results and for opening a sibling/deep-linked
// entity that isn't necessarily part of the currently loaded group list.
function enrichmentToEntityGroup(enrichment, key) {
  return {
    key: key || groupKey(enrichment),
    entity_name: enrichment.entity_name,
    entity_type: enrichment.entity_type,
    entity_subtype: enrichment.entity_subtype,
    place_data: enrichment.place_data || null,
    enrichments: [enrichment],
    sources: [sourceFromEnrichment(enrichment)],
    // Built here (once, during grouping) rather than re-derived by the modal
    // on every open/nav — mirrors Places.jsx's groupedPlaces, which builds
    // creator_contexts inline in its single O(n) pass too.
    creator_contexts: enrichment.creator_context
      ? [{ text: enrichment.creator_context, item_headline: enrichment.item_headline, item_id: enrichment.item_id }]
      : [],
    latest_created_at: enrichment.created_at || null,
  };
}

export default function Entities() {
  const { user, logout } = useAuth();
  useScrollRestoration();
  // The dead `/entities?enrichment=` deep link (COL-87): CollectionDetail
  // has always navigated here with it, but nothing ever read it back. The
  // entity modal's open/closed state now lives in this param the same way
  // Gallery's item modal does (same useUrlModalParam hook). The id in the
  // URL addresses a single enrichment (mention), but COL-94 groups mentions
  // into one card per entity — `selectedGroup` is the group containing
  // whichever mention id is in the URL, not the mention itself.
  const {
    selectedId: selectedEnrichmentId,
    open: openEnrichmentParam,
    close: handleCloseModal,
    setSearchParamsRef,
  } = useUrlModalParam('enrichment');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBoxResetKey, setSearchBoxResetKey] = useState(0);
  const [sortBy, setSortBy] = useState('newest');
  const [showUpload, setShowUpload] = useState(false);
  const [selectedItemForModal, setSelectedItemForModal] = useState(null);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(new Set());
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);

  const entityType = selectedCategory === 'all' ? undefined : selectedCategory;
  const entitySubtype = selectedSubcategory || undefined;

  const toastTimeoutRef = useRef(null);
  const showToast = useCallback((message, type = 'info') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ message, type });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // Fetch entity type counts for sidebar. Cache-first — instant on revisit.
  // enabled: !!user?.id is a second, query-level guard against ever firing
  // under the shared 'anon' key namespace (see queries/keys.js) — belt and
  // braces alongside ProtectedRoute never mounting this page pre-auth.
  const { data: entityCounts } = useQuery({
    queryKey: queryKeys.entityTypeCounts(user?.id),
    queryFn: () => api.getEntityTypeCounts(),
    enabled: !!user?.id,
  });

  // Fetch all enrichments for the current filter (paginate through all
  // results internally, mirroring Places.jsx's fetchAllPlaceEnrichments).
  // COL-94: grouping needs the full set to dedupe correctly — a group split
  // across infinite-scroll pages would under-count mentions — so this page
  // switched from useInfiniteQuery to an eager fetch-all, same as Places.
  const fetchAllEnrichments = useCallback(async () => {
    const allEnrichments = [];
    let cursor = null;
    let pages = 0;
    do {
      const data = await api.getEnrichments({
        status: 'complete',
        entityType,
        entitySubtype,
        limit: PAGE_SIZE,
        ...(cursor ? { cursor } : {}),
      });
      allEnrichments.push(...(data.enrichments || []));
      cursor = data.next_cursor || null;
      pages++;
    } while (cursor && pages < MAX_PAGES);
    return allEnrichments;
  }, [entityType, entitySubtype]);

  const { data: enrichmentsData, isLoading: loading, isError } = useQuery({
    queryKey: queryKeys.allEnrichments(user?.id, { entityType, entitySubtype }),
    queryFn: fetchAllEnrichments,
    enabled: !!user?.id,
  });
  const enrichments = enrichmentsData ?? [];

  useEffect(() => {
    if (isError) showToast('Failed to load entities', 'error');
  }, [isError, showToast]);

  // Group enrichments into one card per entity (COL-94 — ports the shipped
  // groupedPlaces pattern from Places.jsx).
  const groupedEntities = useMemo(() => {
    const groups = new Map();

    for (const e of enrichments) {
      const key = groupKey(e);

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          entity_name: e.entity_name,
          entity_type: e.entity_type,
          entity_subtype: e.entity_subtype,
          place_data: e.place_data || null,
          enrichments: [],
          sources: [],
          creator_contexts: [],
          latest_created_at: e.created_at || null,
        });
      }

      const group = groups.get(key);
      group.enrichments.push(e);
      group.sources.push(sourceFromEnrichment(e));

      if (e.creator_context) {
        group.creator_contexts.push({
          text: e.creator_context,
          item_headline: e.item_headline,
          item_id: e.item_id,
        });
      }

      if (e.created_at && (!group.latest_created_at || e.created_at > group.latest_created_at)) {
        group.latest_created_at = e.created_at;
      }

      // Use the richest place_data available (place-type entities only)
      if (e.place_data && Object.keys(e.place_data).length > Object.keys(group.place_data || {}).length) {
        group.place_data = e.place_data;
      }
    }

    return Array.from(groups.values());
  }, [enrichments]);

  // COL-49: dataset-wide mean prominence weight, used as the neutral
  // fallback for enrichments with no original_rank (see prominenceWeight()
  // above). Computed once over the full fetched set, not per-group, so
  // every group's fallback reflects the same "what does average prominence
  // look like in this collection" baseline. 0.5 (rank ~2) only applies if
  // literally nothing in the collection has a recorded original_rank.
  const neutralProminenceWeight = useMemo(() => {
    const observed = enrichments
      .map((e) => e.original_rank)
      .filter((r) => typeof r === 'number' && r > 0)
      .map((r) => 1 / r);
    if (observed.length === 0) return 0.5;
    return observed.reduce((sum, w) => sum + w, 0) / observed.length;
  }, [enrichments]);

  // Precomputed once per group (not inside the sort comparator, which the
  // comparator would otherwise re-run O(n log n) times) - mentionCount x
  // mean prominence weight across that entity's own mentions.
  const importanceScoreByKey = useMemo(() => {
    const scores = new Map();
    for (const group of groupedEntities) {
      const weights = group.enrichments.map((e) =>
        prominenceWeight(e.original_rank, neutralProminenceWeight)
      );
      const meanWeight = weights.length
        ? weights.reduce((sum, w) => sum + w, 0) / weights.length
        : neutralProminenceWeight;
      scores.set(group.key, (group.sources?.length || 0) * meanWeight);
    }
    return scores;
  }, [groupedEntities, neutralProminenceWeight]);

  const sortedGroupedEntities = useMemo(() => {
    return [...groupedEntities].sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.latest_created_at || 0) - new Date(b.latest_created_at || 0);
        case 'name':
          return (a.entity_name || '').localeCompare(b.entity_name || '');
        case 'type':
          return (a.entity_type || '').localeCompare(b.entity_type || '');
        case 'mentions':
          return (b.sources?.length || 0) - (a.sources?.length || 0);
        case 'importance':
          return (importanceScoreByKey.get(b.key) || 0) - (importanceScoreByKey.get(a.key) || 0);
        default: // newest
          return new Date(b.latest_created_at || 0) - new Date(a.latest_created_at || 0);
      }
    });
  }, [groupedEntities, sortBy, importanceScoreByKey]);

  // Search core (mutation + searching flag + isSubmit -> includeAnswer
  // mapping) is shared with Gallery/Places via useSearch (COL-138).
  const { searchResults, setSearchResults, searching, search } = useSearch({
    scope: 'entities',
    onError: () => showToast('Search failed', 'error'),
  });

  const handleSearch = useCallback((query, opts = {}) => {
    setSearchQuery(query.trim() ? query : '');
    search(query, opts);
  }, [search]);

  // Items to display: search results (relevance-sorted, one card per result —
  // matches don't get grouped since relevance ranking is per-mention) or
  // grouped browse entities (user-sorted).
  const displayEntities = useMemo(() => {
    if (searchResults) {
      return (searchResults.entities || []).map(e => enrichmentToEntityGroup(e, e.enrichment_id));
    }
    return sortedGroupedEntities;
  }, [searchResults, sortedGroupedEntities]);

  // Index of the group backing the open modal, derived from the URL —
  // recomputed from whatever's currently loaded rather than tracked
  // separately (same pattern as Gallery's item modal, COL-87). A group's
  // "id" for URL purposes is its primary (first) mention's enrichment_id.
  const selectedGroupIndex = selectedEnrichmentId
    ? displayEntities.findIndex(g => g.enrichments.some(e => e.enrichment_id === selectedEnrichmentId))
    : -1;

  // Open (or switch to) an entity's detail modal — history push/replace/
  // no-op rules live in useUrlModalParam (shared with Gallery.jsx's item
  // modal); this just adds the optimistic paint on top, skipped when
  // openEnrichmentParam no-ops because the group shown is already this one.
  const openEntityGroup = useCallback((group) => {
    const primaryId = group.enrichments[0]?.enrichment_id;
    if (!primaryId || !openEnrichmentParam(primaryId)) return;
    setSelectedGroup(group);
  }, [openEnrichmentParam]);

  const handleEntityClick = useCallback((group) => {
    openEntityGroup(group);
  }, [openEntityGroup]);

  // handleCloseModal comes from useUrlModalParam above.

  // Open item detail modal (for the "From Screenshot" / referenced items in
  // the entity modal). COL-94: mirrors Places.jsx's handleOpenItemModal —
  // if the source item is gone (deleted, or a stale reference — enrichments
  // cascade-delete with their item, but this stays defensive; see COL-80 for
  // the analogous chat-card bug) this shows a toast instead of throwing.
  const handleOpenItemModal = useCallback(async (itemId) => {
    try {
      const item = await api.getItem(itemId, { includeRawResponse: true });
      setSelectedItemForModal(item);
    } catch (err) {
      showToast('Failed to load item', 'error');
    }
  }, [showToast]);

  const handleCloseItemModal = useCallback(() => {
    setSelectedItemForModal(null);
  }, []);

  // Open a group modal from a sibling entity (same pattern as Places.jsx's
  // handleOpenPlaceFromSibling) — prefer the already-loaded group so mention
  // counts stay accurate, falling back to an ad-hoc single-enrichment group.
  // Routed through openEntityGroup (not a direct setSelectedGroup) so the
  // URL param stays in sync with whichever entity the modal now shows.
  const handleOpenEntityFromSibling = useCallback((enrichment) => {
    const key = groupKey(enrichment);
    const existing = groupedEntities.find(g => g.key === key);
    openEntityGroup(existing || enrichmentToEntityGroup(enrichment, key));
  }, [groupedEntities, openEntityGroup]);

  // Resolve `selectedGroup` from the URL: reuse an already-loaded group
  // (grid click, instant) or fetch the enrichment directly by id and wrap
  // it into an ad-hoc group — the deep link's whole point (a hard refresh,
  // or a link from CollectionDetail, won't have anything loaded yet). A
  // fetched id that turns out to belong to a different entity-type filter
  // than the one currently selected renders as a single-mention group
  // (everything we know about it) rather than blocking on a second fetch.
  // `attemptedIdRef` stops this from refiring a fetch every time the list
  // changes underneath an id that will never be "known" here.
  const attemptedIdRef = useRef(null);
  useEffect(() => {
    if (!selectedEnrichmentId) {
      setSelectedGroup(null);
      return;
    }
    const known = displayEntities.find(g => g.enrichments.some(e => e.enrichment_id === selectedEnrichmentId));
    if (known) {
      setSelectedGroup(known);
      return;
    }
    if (attemptedIdRef.current === selectedEnrichmentId) return;
    if (!user?.id) return;
    attemptedIdRef.current = selectedEnrichmentId;

    let cancelled = false;
    api.getEnrichments({ enrichmentId: selectedEnrichmentId, limit: 1 })
      .then((data) => {
        if (cancelled) return;
        const found = data.enrichments?.[0];
        if (!found) throw new Error('Enrichment not found');
        setSelectedGroup(enrichmentToEntityGroup(found));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load entity from URL:', err);
        showToast('That entity is unavailable', 'error');
        setSelectedGroup(null);
        // Clear the guard so a later revisit of this same id (a stale link
        // clicked again, browser forward) retries the fetch instead of
        // silently no-oping — the id was never added to `displayEntities`
        // either, so `known` would never become true, and without this
        // reset it would be permanently un-openable for the rest of this
        // page's life.
        attemptedIdRef.current = null;
        setSearchParamsRef.current((prev) => {
          prev.delete('enrichment');
          return prev;
        }, { replace: true });
      });
    return () => { cancelled = true; };
    // setSearchParams intentionally excluded — see setSearchParamsRef above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnrichmentId, displayEntities, user?.id, showToast]);

  const handlePrevEntity = useCallback(() => {
    if (selectedGroupIndex > 0) {
      openEntityGroup(displayEntities[selectedGroupIndex - 1]);
    }
  }, [selectedGroupIndex, displayEntities, openEntityGroup]);

  const handleNextEntity = useCallback(() => {
    if (selectedGroupIndex < displayEntities.length - 1) {
      openEntityGroup(displayEntities[selectedGroupIndex + 1]);
    }
  }, [selectedGroupIndex, displayEntities, openEntityGroup]);

  const categories = useMemo(() => {
    const ENTITY_TYPE_LABELS = {
      place: 'Places',
      person: 'People',
      organization: 'Organizations',
      event: 'Events',
      creative_work: 'Creative Works',
      product: 'Products',
      reference: 'References',
    };

    if (!entityCounts) {
      return [{ id: 'all', name: 'All', count: 0 }];
    }

    const typeCats = entityCounts.entity_types
      .map(et => ({
        id: et.type,
        name: ENTITY_TYPE_LABELS[et.type] || et.type,
        count: et.count,
        subcategories: (et.subtypes || []).map(sub => ({
          id: sub.subtype,
          name: sub.subtype,
          count: sub.count,
          disabled: !!sub.is_overflow,
        })),
      }))
      .sort((a, b) => b.count - a.count);

    return [
      { id: 'all', name: 'All', count: entityCounts.total },
      ...typeCats,
    ];
  }, [entityCounts]);

  return (
    <div className="app-layout">
      <Sidebar
        categories={categories}
        selectedCategory={selectedCategory}
        selectedSubcategory={selectedSubcategory}
        onSelectCategory={(id, subId) => {
          setSelectedCategory(id);
          setSelectedSubcategory(subId || null);
          setSearchResults(null);
          setSearchQuery('');
          setSearchBoxResetKey(k => k + 1);
        }}
        user={user}
        onLogout={logout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className="main-content">
        <header className="content-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <button
              className="btn btn-ghost btn-icon sidebar-toggle"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation"
            >
              <List size={20} />
            </button>
            <Link
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '0.875rem',
                color: 'var(--text-muted)',
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              <CaretLeft size={16} />
              Items
            </Link>
            <h1 className="page-title">Entities</h1>
            {searchResults && (
              <span style={{
                fontSize: '0.875rem',
                color: 'var(--text-muted)',
                marginLeft: 'var(--space-md)',
              }}>
                {searchResults.entities?.length || 0} results
              </span>
            )}
          </div>

          <div className="header-actions">
            <SearchBox onSearch={handleSearch} placeholder="Search entities..." key={searchBoxResetKey} />

            {!searchResults && (
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}

            <button
              className="btn btn-ghost"
              onClick={() => {
                if (isSelectMode) { setIsSelectMode(false); setSelectedGroupKeys(new Set()); }
                else setIsSelectMode(true);
              }}
            >
              {isSelectMode ? 'Cancel' : 'Select'}
            </button>

            <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
              <PlusIcon />
              <span>Upload</span>
            </button>
          </div>
        </header>

        {/* AI Answer (when searching) */}
        <SearchSummary
          answer={searchResults?.answer}
          onItemClick={(item) => handleOpenItemModal(item.item_id)}
        />

        <div className="content-body">
          {(loading || searching) ? (
            <CardGridSkeleton />
          ) : displayEntities.length === 0 ? (
            <EmptyState
              title={searchQuery ? 'No matching entities' : 'No entities found'}
              description={searchQuery
                ? 'Try different search terms or clear the search'
                : entityType
                  ? 'Try selecting a different entity type'
                  : 'Entities will appear here after items are analyzed and enriched'}
            />
          ) : (
            <motion.div
              className="items-grid"
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.03 } } }}
            >
              <AnimatePresence mode="sync">
                {displayEntities.map((group, index) => {
                  const groupKeyId = group.key;
                  return (
                    <div key={groupKeyId} style={{ position: 'relative' }}>
                      {isSelectMode && (
                        <div
                          className={`collection-item-checkbox ${selectedGroupKeys.has(groupKeyId) ? 'checked' : ''}`}
                          style={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}
                        >
                          {selectedGroupKeys.has(groupKeyId) && (
                            <Check size={14} />
                          )}
                        </div>
                      )}
                      <EntityCard
                        group={group}
                        index={index}
                        onClick={() => {
                          if (isSelectMode) {
                            setSelectedGroupKeys(prev => {
                              const next = new Set(prev);
                              if (next.has(groupKeyId)) next.delete(groupKeyId);
                              else next.add(groupKeyId);
                              return next;
                            });
                          } else {
                            handleEntityClick(group);
                          }
                        }}
                      />
                    </div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Bottom spacer */}
          {displayEntities.length > 0 && (
            <div style={{ minHeight: '60px' }} />
          )}
        </div>
      </main>

      <AnimatePresence>
        {selectedGroup && (
          <EntityDetailModal
            group={selectedGroup}
            onClose={handleCloseModal}
            onPrev={handlePrevEntity}
            onNext={handleNextEntity}
            hasPrev={selectedGroupIndex > 0}
            hasNext={selectedGroupIndex < displayEntities.length - 1}
            itemIndex={selectedGroupIndex >= 0 ? selectedGroupIndex : undefined}
            totalItems={selectedGroupIndex >= 0 ? displayEntities.length : undefined}
            onOpenItem={handleOpenItemModal}
            onOpenEntity={handleOpenEntityFromSibling}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedItemForModal && (
          <ItemDetailModal
            item={selectedItemForModal}
            onClose={handleCloseItemModal}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showUpload && (
          <UploadModal
            onClose={() => setShowUpload(false)}
            onUpload={(newItem) => {
              setShowUpload(false);
              showToast('Screenshot uploaded! It will appear here after processing.', 'success');
            }}
          />
        )}
      </AnimatePresence>

      <Toast toast={toast} />

      <AnimatePresence>
        {isSelectMode && selectedGroupKeys.size > 0 && (
          <SelectionBar
            count={selectedGroupKeys.size}
            onAddToCollection={() => setShowCollectionPicker(true)}
            onCancel={() => { setIsSelectMode(false); setSelectedGroupKeys(new Set()); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCollectionPicker && (
          <CollectionPickerModal
            // A "selected entity" now maps to one or more enrichment rows —
            // add every mention in each selected group, not just the first.
            // Looked up from displayEntities (not groupedEntities) so this
            // also works while search results are showing — those groups
            // are single-enrichment ad-hoc groups keyed by enrichment_id,
            // not present in the browse-mode groupedEntities list.
            items={displayEntities
              .filter(g => selectedGroupKeys.has(g.key))
              .flatMap(g => g.enrichments.map(e => ({ item_id: e.enrichment_id, item_type: 'enrichment' })))}
            onClose={() => setShowCollectionPicker(false)}
            onSuccess={(msg) => {
              showToast(msg, 'success');
              setIsSelectMode(false);
              setSelectedGroupKeys(new Set());
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function PlusIcon() {
  return <Plus size={18} />;
}

function EntityCard({ group, index, onClick }) {
  const { entity_name, entity_type, place_data, sources = [], enrichments = [] } = group;
  const hasPlaceData = place_data && entity_type === 'place';
  const primaryEnrichment = enrichments[0] || {};
  const firstSource = sources[0] || {};
  // COL-103 (owner decision 2026-08-17): Google Places photos are no longer
  // requested, stored or rendered. The item screenshot is the only image —
  // unconditionally, not as a fallback behind a failed photo lookup.
  const displayImage = firstSource.item_image;

  // Accessible name states what the card IS (entity name + type), not "card"
  // or "button".
  const accessibleName = cardAccessibleName(entity_name, entity_type);

  return (
    <motion.button
      type="button"
      className="entity-card card-button-reset"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ delay: index * 0.03 }}
      onClick={onClick}
      aria-label={accessibleName}
    >
      {displayImage && (
        <div className="entity-card-thumbnail">
          <img src={displayImage} alt="" loading="lazy" />
        </div>
      )}

      <div className="entity-card-body">
        <div className="enrichment-card-header">
          <span className="enrichment-card-entity">{entity_name}</span>
          <span className="enrichment-card-badge">{entity_type}</span>
        </div>

        {firstSource.item_headline && (
          <div className="entity-card-headline">{firstSource.item_headline}</div>
        )}

        {hasPlaceData && (
          <div className="entity-card-place">
            {place_data.formatted_address && <span>{place_data.formatted_address}</span>}
            {place_data.rating && <span> · ★ {place_data.rating}</span>}
          </div>
        )}

        {(primaryEnrichment.creator_context || primaryEnrichment.answer) && (
          <div className="entity-card-answer">{primaryEnrichment.creator_context || primaryEnrichment.answer}</div>
        )}

        {sources.length > 1 && (
          <div className="entity-card-mentions">From {sources.length} items</div>
        )}
      </div>
    </motion.button>
  );
}
